import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from "@angular/core";
import { MatIconButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { toolbarOptions } from "../../shared/model/toolbar-constants";
import { ToolbarService } from "../../viewer-components/services/internal-services/toolbar.service";
import { ChatContainerComponent, ChatPanelHdrComponent } from "../../ai-components/chat";
import type { AgentDefinition } from "../../ai-components/chat/model/agent.types";
import type { ToolRendererConfig } from "../../ai-components/chat/model/tool-data.types";
import { DebugModeService } from "../../ai-components/chat/services/debug-mode.service";
import { DebugStreamService } from "../../ai-components/chat/services/debug-stream.service";
import { AiChatSessionService } from "../../ai-components/chat/services/ai-chat-session.service";

/**
 * Left-sidebar toolbar component that provides access to the AI chat panel.
 *
 * @example
 * <app-ai-chat-toolbar
 *   [enabledTool]="toolbarService.enabledTool$ | async"
 *   [agents]="[{ id: 'my_agent', label: 'AI Chat' }]" />
 */
@Component({
  selector: "app-ai-chat-toolbar",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIcon, MatIconButton, MatTooltip, ChatContainerComponent, ChatPanelHdrComponent],
  templateUrl: "./ai-chat-toolbar.component.html",
  styleUrls: ["./ai-chat-toolbar.component.scss"],
})
export class AiChatToolbarComponent implements OnInit, OnDestroy {
  private readonly minPanelWidth = 300;

  private readonly maxPanelWidth = 900;

  private resizeStartX = 0;

  private resizeStartWidth = 0;

  private readonly boundResizeMove = this.onResizeMove.bind(this);

  private readonly boundResizeUp = this.onResizeUp.bind(this);

  @ViewChild("panelContainer") private panelContainer!: ElementRef<HTMLElement>;

  private readonly toolbarService = inject(ToolbarService);

  private readonly chatSession = inject(AiChatSessionService);

  protected readonly debugMode = inject(DebugModeService);

  protected readonly debugStream = inject(DebugStreamService);

  readonly toolbarOption = toolbarOptions.AI_CHAT;

  displayPanel = false;

  /** List of agents available in the chat panel. */
  @Input() agents: AgentDefinition[] = [];

  /** Optional custom tool renderers. */
  @Input() toolRenderers: ToolRendererConfig[] = [];

  /** Drives panel visibility from the shared toolbar state. */
  @Input() set enabledTool(tool: string) {
    this.displayPanel = this.toolbarOption === tool;
  }

  /** Direct references to the shared signals — stable for Angular's OnPush tracking. */
  protected readonly currentThreadId = this.chatSession.threadId;

  protected readonly selectedAgentId = this.chatSession.agentId;

  protected readonly activeAgent = computed(() => this.agents.find((a) => a.id === this.chatSession.agentId()) ?? null);

  /** Run error propagated from the chat component; shown below the panel header. */
  protected readonly panelRunError = signal<string | null>(null);

  ngOnInit(): void {
    if (this.agents.length >= 1 && this.chatSession.agentId() === null) {
      this.chatSession.agentId.set(this.agents[0].id);
    }
  }

  ngOnDestroy(): void {
    document.removeEventListener("mousemove", this.boundResizeMove);
    document.removeEventListener("mouseup", this.boundResizeUp);
  }

  protected onResizeStart(event: MouseEvent): void {
    event.preventDefault();
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.panelContainer.nativeElement.getBoundingClientRect().width;
    document.addEventListener("mousemove", this.boundResizeMove);
    document.addEventListener("mouseup", this.boundResizeUp);
  }

  private onResizeMove(event: MouseEvent): void {
    const dx = event.clientX - this.resizeStartX;
    const newWidth = Math.min(this.maxPanelWidth, Math.max(this.minPanelWidth, this.resizeStartWidth + dx));
    this.panelContainer.nativeElement.style.setProperty("--vx-panel-w", `${newWidth}px`);
  }

  private onResizeUp(): void {
    document.removeEventListener("mousemove", this.boundResizeMove);
    document.removeEventListener("mouseup", this.boundResizeUp);
  }

  togglePanelDisplay(): void {
    this.toolbarService.enableTool(!this.displayPanel ? this.toolbarOption : toolbarOptions.NONE);
  }

  protected onAgentChange(agentId: string): void {
    this.chatSession.switchAgent(agentId);
  }

  protected startNewChat(): void {
    this.chatSession.startNewChat();
    this.panelRunError.set(null);
  }
}
